// src/hooks/use-auth.ts
'use client';

import * as React from 'react';
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type User,
} from 'firebase/auth';
import { getFirebaseApp } from '@/lib/firebase/client';
import {
  createRolePrivilegeFlags,
  defaultPrivilegesForRole,
  ROLE_PRIVILEGE_KEYS,
  isRole,
  type Role,
  type RolePrivilegeFlags,
} from '@/lib/roles';
import { ACKNOWLEDGEMENTS_ENABLED } from '@/config/features';

export type AuthCtx = {
  user: User | null;
  loading: boolean;
  role: Role | null;
  claims: Record<string, unknown> | null;
  privileges: RolePrivilegeFlags;
  canViewDashboards: boolean;
  canViewPortal: boolean;
  canEditPortal: boolean;
  canAcknowledgePolicies: boolean;
  canManageAcknowledgements: boolean;
  canManagePolicies: boolean;
  canManageRisks: boolean;
  canEditRiskRegister: boolean;
  canManageTreatmentPlans: boolean;
  canReviewRiskDecisions: boolean;
  canEditDocuments: boolean;
  canManageUsers: boolean;
  canManageRoles: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshIdToken: () => Promise<void>;
};

const AuthContext = React.createContext<AuthCtx | null>(null);

type TokenClaims = Record<string, unknown> & { role?: unknown; privileges?: unknown };

function parsePrivileges(value: unknown, role: Role | null): RolePrivilegeFlags {
  const fallback = defaultPrivilegesForRole(role);
  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const record = value as Record<string, unknown>;
  const overrides: Partial<RolePrivilegeFlags> = {};

  for (const key of ROLE_PRIVILEGE_KEYS) {
    if (key in record) {
      overrides[key] = Boolean(record[key]);
    }
  }

  return createRolePrivilegeFlags({ ...fallback, ...overrides });
}

// Rough detector for iOS Safari where popups are often blocked
const prefersRedirect = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS && isSafari;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [claims, setClaims] = React.useState<Record<string, unknown> | null>(null);
  const [role, setRole] = React.useState<Role | null>(null);
  const [privileges, setPrivileges] = React.useState<RolePrivilegeFlags>(() => createRolePrivilegeFlags());
  const isMountedRef = React.useRef(true);

  const app = React.useMemo(() => getFirebaseApp(), []);
  const auth = React.useMemo(() => getAuth(app), [app]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const applyClaims = React.useCallback(
    (raw: TokenClaims | null | undefined) => {
      if (!isMountedRef.current) return;
      const normalized = raw ? { ...raw } : null;
      setClaims(normalized);
      const potentialRole = normalized?.role;
      const parsedRole = isRole(potentialRole) ? potentialRole : null;
      setRole(parsedRole);
      setPrivileges(parsePrivileges(normalized?.privileges, parsedRole));
    },
    []
  );

  const loadClaims = React.useCallback(
    async (targetUser: User, forceRefresh = false): Promise<TokenClaims | null> => {
      try {
        const token = await targetUser.getIdTokenResult(forceRefresh);
        const raw = token.claims as TokenClaims;
        applyClaims(raw);
        return raw;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
          `[Auth] getIdTokenResult(${forceRefresh ? 'force' : 'passive'}) error`,
          msg
        );
        applyClaims(null);
        return null;
      }
    },
    [applyClaims]
  );

  const refreshIdToken = React.useCallback(async () => {
    const current = auth.currentUser;
    if (!current) {
      applyClaims(null);
      return;
    }

    if (!isMountedRef.current) return;

    setLoading(true);
    try {
      const refreshed = await loadClaims(current, true);
      if (!isRole(refreshed?.role)) {
        console.warn('[Auth] refreshIdToken did not yield a role claim', {
          uid: current.uid,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [auth, applyClaims, loadClaims]);

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      console.log('[Auth] Initializing…');

      // 1) Persistence (best → fallback)
      try {
        await setPersistence(auth, indexedDBLocalPersistence);
        console.log('[Auth] Persistence = indexedDB');
      } catch {
        try {
          await setPersistence(auth, browserLocalPersistence);
          console.log('[Auth] Persistence = localStorage');
        } catch {
          await setPersistence(auth, browserSessionPersistence);
          console.log('[Auth] Persistence = sessionStorage');
        }
      }

      // 2) Process redirect result if any (safe no-op otherwise)
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log('[Auth] Redirect sign-in success:', result.user.email);
          if (mounted) setUser(result.user);
        } else {
          console.log('[Auth] No redirect result');
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[Auth] getRedirectResult error', msg);
      }

      // 3) Subscribe to auth state
      const unsub = onAuthStateChanged(auth, (u) => {
        console.log('[Auth] onAuthStateChanged =>', u?.email ?? 'null');
        if (!mounted || !isMountedRef.current) return;

        if (!u) {
          setUser(null);
          applyClaims(null);
          setLoading(false);
          return;
        }

        setUser(u);
        setLoading(true);
        applyClaims(null);

        void (async () => {
          try {
            const claims = await loadClaims(u, false);
            if (!mounted || !isMountedRef.current) return;

            const maybeRole = claims?.role;
            if (!isRole(maybeRole)) {
              console.warn('[Auth] Missing role claim after initial fetch. Retrying with force refresh…', {
                uid: u.uid,
              });
              await new Promise((resolve) => setTimeout(resolve, 800));
              if (!mounted || !isMountedRef.current) return;
              const refreshed = await loadClaims(u, true);
              if (!isRole(refreshed?.role)) {
                console.error('[Auth] No role claim present after forced refresh', {
                  uid: u.uid,
                });
              }
            }
          } finally {
            if (mounted && isMountedRef.current) {
              setLoading(false);
            }
          }
        })();
      });

      // 4) Light sanity check after redirect path
      setTimeout(() => {
        if (mounted && !auth.currentUser) {
          console.warn(
            '[Auth] Still no user after redirect. currentUser =',
            auth.currentUser
          );
        }
      }, 1500);

      return () => {
        mounted = false;
        unsub();
      };
    })();
  }, [auth, applyClaims, loadClaims]);

  const signInWithGoogle = React.useCallback(async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      hd: 'docplanner.com',
      prompt: 'select_account',
    });

    // Prefer POPUP except on iOS Safari (where popups are often blocked)
    if (!prefersRedirect()) {
      try {
        console.log('[Auth] Starting Google POPUP sign-in…');
        const result = await signInWithPopup(auth, provider);
        console.log('[Auth] Popup sign-in success:', result.user.email);
        setUser(result.user);
        return;
      } catch (err: unknown) {
        // Narrow error code if available
        const code = (err as { code?: string } | null)?.code ?? '';
        console.error('[Auth] Popup sign-in failed:', code, err);

        // If popup is blocked or not allowed, fall back to redirect
        const shouldFallback =
          code === 'auth/popup-blocked' ||
          code === 'auth/operation-not-allowed' ||
          code === 'auth/unauthorized-domain';

        if (!shouldFallback) {
          // If the user closed the popup, don't force redirect.
          if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            return;
          }
        } else {
          console.warn('[Auth] Falling back to REDIRECT sign-in…');
        }
      }
    }

    console.log('[Auth] Starting Google REDIRECT sign-in…');
    await signInWithRedirect(auth, provider);
  }, [auth]);

  const signOut = React.useCallback(async () => {
    console.log('[Auth] Signing out…');
    await auth.signOut();
    if (!isMountedRef.current) return;
    setUser(null);
    applyClaims(null);
    setLoading(false);
    setPrivileges(createRolePrivilegeFlags());
  }, [auth, applyClaims]);

  const value: AuthCtx = React.useMemo(() => {
    const acknowledgementsEnabled = ACKNOWLEDGEMENTS_ENABLED;
    const canManagePolicies = privileges.managePolicies || privileges.editDocuments;
    const canEditRiskRegister =
      privileges.manageRiskRegister || privileges.manageRisks || privileges.editDocuments;
    const canManageTreatmentPlans =
      privileges.manageTreatmentPlans || privileges.manageRisks || privileges.editDocuments;
    const canReviewRiskDecisions =
      privileges.reviewRiskDecisions || privileges.manageRisks || privileges.editDocuments;
    const canManageRisks = canEditRiskRegister || canManageTreatmentPlans || canReviewRiskDecisions;
    const canEditPortal = privileges.editPortal || canManagePolicies;
    const canAcknowledgePolicies =
      acknowledgementsEnabled &&
      (privileges.acknowledgePolicies || privileges.editPortal || privileges.manageAcknowledgements);
    const canManageAcknowledgements =
      acknowledgementsEnabled &&
      (privileges.manageAcknowledgements || privileges.managePolicies || privileges.manageRoles);
    return {
      user,
      loading,
      role,
      claims,
      privileges,
      canViewDashboards: privileges.viewDashboards,
      canViewPortal: privileges.viewPortal || canEditPortal,
      canEditPortal,
      canAcknowledgePolicies,
      canManageAcknowledgements,
      canManagePolicies,
      canManageRisks,
      canEditRiskRegister,
      canManageTreatmentPlans,
      canReviewRiskDecisions,
      canEditDocuments: canManagePolicies,
      canManageUsers: privileges.manageUsers,
      canManageRoles: privileges.manageRoles,
      signInWithGoogle,
      signOut,
      refreshIdToken,
    };
  }, [
    user,
    loading,
    role,
    claims,
    privileges,
    signInWithGoogle,
    signOut,
    refreshIdToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}